import { useMemo, useState, type ChangeEvent } from "react";
import {
  Box,
  Button,
  Card,
  CardHeader,
  CardBody,
  Heading,
  HStack,
  VStack,
  Text,
  Input,
  InputGroup,
  InputRightElement,
  Select,
  Tabs,
  TabList,
  Tab,
  Badge,
  IconButton,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
  DrawerCloseButton,
  useDisclosure,
  SimpleGrid,
} from "@chakra-ui/react";
import { Search } from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  DataTable,
  type DataTableOptions,
} from "@/components/tables";
import useBasePageHook from "@/hooks/useBasePageHook";
import apiClient from "@/lib/api/client";
import BasePageLayout from "@/components/layout/BasePageLayout";

const ROOT_URL = "user-management/mobile-devices";

type ActionT = "force" | "deactivate";
export default function MobileDevicesPage() {
  const {
    tableRef,
    searchRef,
    activeTab,
    searchQuery,
    totalRows,
    totalTrash,
    statusFilterOptions,
    permissions,
    colors,
    access,
    showSubSubCallback,
    handleTabChange,
    handleSearch,
    handleKeyDown,
    showSuccess,
    showError,
    showWarning,
  } = useBasePageHook();
  const [bulkAction, setBulkAction] = useState<ActionT | string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [action, setAction] = useState<ActionT | string>("");
  const [modalMessage, setModalMessage] = useState("");
  const [selectedId, setSelectedId] = useState<string | number[] | null>(null);
  const [detailDevice, setDetailDevice] = useState<Record<string, unknown> | null>(null);
  const navigate = useNavigate();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isDetailOpen, onOpen: onDetailOpen, onClose: onDetailClose } = useDisclosure();

  const validate = (): boolean => {
    if (!selectedId) {
      showError("Please select a device.");
      onClose();
      return false;
    }
    if (action.length === 0) {
      showError("Please select an action.");
      onClose();
      return false;
    }

    return true;
  };
  const confirmPrompt = ({
    id = null,
    action = null,
  }: {
    id: string | null;
    action: ActionT | null;
  }) => {
    const selectedIds = tableRef.current?.getSelectedRows() || [];
    setSelectedId(id ?? selectedIds);
    setAction((action ?? bulkAction.length === 0) ? "" : bulkAction);
    let actionDescription =
      action === "deactivate" ? "Deactivate" : "Force Logout";
    setModalMessage(
      `Are you sure you want to ${actionDescription} this user device?`,
    );
    onOpen();
  };
  const handleSubmit = async () => {
    if (!validate()) return false;

    const successMessage =
      action === "deactivate" ? "Deactivated" : "Forced Logout";
    const errorMessage =
      action === "deactivate" ? "Deactivation" : "Force Logout";
    let path = action === "force" ? "force-logout" : "deactivate";
    let url = `${ROOT_URL}/${selectedId}/${path}`;
    let payload = null;

    if (Array.isArray(selectedId)) {
      url = `${ROOT_URL}/bulk-update`;
      payload = { ids: selectedId, action: bulkAction };
    }
    try {
      setIsLoading(true);
      await apiClient.put(url, payload).then(() => {
        showSuccess(`Successfully ${successMessage}.`);
        tableRef.current?.reload();
      });
    } catch (error) {
      showError(`Error while ${errorMessage}.`);
    } finally {
      onClose();
      setIsLoading(false);
      setBulkAction("");
    }
  };
  const tableOptions: DataTableOptions = useMemo(
    () => ({
      dataFields: {
        user_full_name: {
          name: "Name",
          withSort: true,
          maxWidth: "200px",
          render(value, row) {
            return <>{String(value).toUpperCase()}</>;
          },
        },
        platform: {
          name: "Platform",
          withSort: true,
          maxWidth: "200px",
        },
        model: {
          name: "Model",
          withSort: true,
          maxWidth: "250px",
        },
        os_version: {
          name: "Version",
          withSort: true,
          maxWidth: "50px",
          render(value, row) {
            return <>{value === 0 ? "" : value}</>;
          },
        },
        app_version: {
          name: "App Version",
          withSort: true,
          maxWidth: "200px",
        },
        status: {
          name: "Status",
          withSort: true,
          withFilter: true,
          filterOptions: statusFilterOptions,
          badge: {
            online: "green",
            idle: "gray",
            offline: "yellow",
          },
          badgeLabels: {
            online: "Online",
            idle: "Idle",
            offline: "Offline",
          },
        },
        last_heartbeat_at: {
          name: "Last Heartbeat",
          withSort: true,
          maxWidth: "160px",
          render(value) {
            return <>{value ? String(value) : "—"}</>;
          },
        },
        battery_percent: {
          name: "Battery (%)",
          withSort: true,
          maxWidth: "100px",
          render(value, row) {
            const battery = row?.battery_percent ?? "0%";
            return <>{battery}</>;
          },
        },
      },
      dataSource: "/user-management/mobile-devices",
      primaryKey: "id",
      softDelete: true,
      displayInModal: false,
      noRecordText: "No device found",
      otherActions: [
        {
          name: "View Details",
          onClick: (row) => {
            setDetailDevice(row ?? null);
            onDetailOpen();
          },
        },
        {
          name: "Force Logout",
          onClick: (row) => {
            if (row?.status === "offline") {
              return showWarning("Device is already offline.");
            }
            confirmPrompt({ id: row?.id, action: "force" });
          },
        },
        {
          name: "Deactivate",
          onClick: (row) => {
            if (row?.active === "Inactive") {
              return showWarning("Device is already inactive");
            }
            confirmPrompt({ id: row?.id, action: "deactivate" });
          },
        },
      ],
    }),
    [activeTab, statusFilterOptions, onDetailOpen],
  );

  const tableParams = useMemo(() => ({ search: searchQuery }), [searchQuery]);

  return (
    <BasePageLayout
      mainBoxProps={{
        p: 4,
        h: "100%",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <VStack spacing={2} align="stretch" flex="1" overflow="hidden" minH={0}>
        <Card
          flexShrink={0}
          display="flex"
          flexDirection="column"
          flex="1"
          minH={0}
          overflow="hidden"
        >
          <CardHeader flexShrink={0} py={3}>
            <Heading size="md" mb={1}>
              Mobile Device Registry
            </Heading>
            <Text fontSize="sm" color={colors.text}>
              Manage mobile devices
            </Text>
          </CardHeader>

          <CardHeader py={0} px={0} flexShrink={0}>
            <Box pt={2} pb={0}>
              <HStack
                justify="space-between"
                align="flex-end"
                spacing={4}
                px={4}
                width="100%"
              >
                <Tabs
                  index={activeTab === "all" ? 0 : 1}
                  onChange={handleTabChange}
                >
                  <TabList borderBottom="none">
                    <Tab>
                      All{" "}
                      <Badge ml={2} colorScheme="blue">
                        {totalRows}
                      </Badge>
                    </Tab>
                    {totalTrash > 0 && (
                      <Tab>
                        Trash{" "}
                        <Badge ml={2} colorScheme="red">
                          {totalTrash}
                        </Badge>
                      </Tab>
                    )}
                  </TabList>
                </Tabs>
              </HStack>
              <Box borderBottom="2px" borderColor={colors.border} mx={4} />
            </Box>
          </CardHeader>

          <CardHeader py={1} pt={3} pb={2} flexShrink={0}>
            <HStack spacing={4} flexWrap="wrap">
              {access.can_delete && (
                <HStack spacing={2} minW="250px">
                  <Select
                    placeholder="Bulk actions"
                    size="sm"
                    value={bulkAction}
                    onChange={(e: ChangeEvent<HTMLSelectElement>) => {
                      e.stopPropagation();
                      setBulkAction(e.target.value);
                    }}
                  >
                    {activeTab === "trash" && (
                      <option value="restore">Restore</option>
                    )}
                    {activeTab === "trash" && (
                      <option value="delete">Delete Permanently</option>
                    )}
                    {activeTab === "all" && (
                      <option value="force">Force Logout</option>
                    )}
                    {activeTab === "all" && (
                      <option value="deactivate">Deactivate</option>
                    )}
                  </Select>
                  <Button
                    disabled={bulkAction.length === 0}
                    size="sm"
                    colorScheme="blue"
                    onClick={() => confirmPrompt({ id: null, action: null })}
                  >
                    Apply
                  </Button>
                </HStack>
              )}

              <Box flex="0 1 auto" minW="250px" maxW="400px">
                <InputGroup size="sm">
                  <Input
                    ref={searchRef}
                    placeholder="Search by name..."
                    onKeyDown={handleKeyDown}
                    defaultValue={searchQuery}
                    pr="2.5rem"
                  />
                  <InputRightElement width="2.5rem">
                    <IconButton
                      aria-label="Search"
                      icon={<Search size={16} />}
                      size="sm"
                      h="1.75rem"
                      w="1.75rem"
                      onClick={handleSearch}
                      variant="ghost"
                    />
                  </InputRightElement>
                </InputGroup>
              </Box>
            </HStack>
          </CardHeader>

          <CardBody
            p={0}
            pt={1}
            pb={4}
            mx={5}
            flex="1"
            overflow="hidden"
            display="flex"
            flexDirection="column"
            minH={0}
            position="relative"
          >
            <Box h="100%" display="flex" flexDirection="column" minH={0}>
              <DataTable
                ref={tableRef}
                options={tableOptions}
                params={tableParams}
                access={permissions}
                setSubSub={showSubSubCallback}
                onOpenModal={(id) => navigate(`/user-management/roles/${id}`)}
                filterOptions={{
                  active: statusFilterOptions,
                }}
              />
            </Box>
          </CardBody>
        </Card>
      </VStack>
      <Modal isOpen={isOpen} onClose={onClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Confirmation</ModalHeader>
          <ModalBody>
            <Text>{modalMessage}</Text>
          </ModalBody>
          <ModalFooter>
            <Button
              variant="ghost"
              mr={3}
              onClick={onClose}
              isDisabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleSubmit}
              isLoading={isLoading}
              loadingText="Processing..."
            >
              Confirm
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      <Drawer isOpen={isDetailOpen} onClose={onDetailClose} size="md">
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader>Device Details</DrawerHeader>
          <DrawerBody>
            {detailDevice ? (
              <SimpleGrid columns={1} spacing={3} fontSize="sm">
                <Text><strong>User:</strong> {String(detailDevice.user_full_name ?? "—")}</Text>
                <Text><strong>Platform:</strong> {String(detailDevice.platform ?? "—")}</Text>
                <Text><strong>Model:</strong> {String(detailDevice.model ?? "—")}</Text>
                <Text><strong>OS Version:</strong> {String(detailDevice.os_version ?? "—")}</Text>
                <Text><strong>App Version:</strong> {String(detailDevice.app_version ?? "—")}</Text>
                <Text><strong>Status:</strong> {String(detailDevice.status ?? "—")}</Text>
                <Text><strong>Last Heartbeat:</strong> {String(detailDevice.last_heartbeat_at ?? "—")}</Text>
                <Text><strong>Battery:</strong> {String(detailDevice.battery_percent ?? "—")}%</Text>
                <Text><strong>Device UUID:</strong> {String(detailDevice.device_uuid ?? "—")}</Text>
                <Text><strong>Active:</strong> {String(detailDevice.active ?? "—")}</Text>
              </SimpleGrid>
            ) : (
              <Text color="gray.500">No device selected</Text>
            )}
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </BasePageLayout>
  );
}
